import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/common/vehicle/VehicleDetail';
import { ROUTER_ESTIMATE } from 'app/AppRouter';
import { RootState } from 'app/AppStore';
import { trackAppEvent } from 'app/AppTracker';
import { SaveIcon } from 'assets/icons';
import { Dialog, useDisclosure } from 'components/Dialog';
import { getLastVehicleDetail } from 'domains/catalog/Catalog.store';
import { useFetchAvailableDMSServices } from 'domains/dms/DMS.requests';
import { createEstimateFromDMSRequest, createNewEstimate } from 'domains/estimate/Estimate.store';
import { getAvailableDMSService } from 'domains/user';
import { Box, CenterFlex, Container, Flex, Text, WhiteButton } from 'UI';
import {
  TRACKING_EVENT_CREATE_NEW_ESTIMATE,
  TRACKING_EVENT_CREATE_NEW_ESTIMATE_CONFIRM,
} from 'utils/eventTracker/EventTracker.types';
import CreateEstimateButton from './CreateEstimateButton';
import { SPopover } from './EstimatePage.styled';
import HistoryTab from './HistoryTab';

export const HistoryPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const vehicle = useSelector(getLastVehicleDetail) as VehicleDetail;
  const isDMSAvailable = useSelector((state: RootState) => getAvailableDMSService(state, 'ESTIMATE'));
  const disclosure = useDisclosure();
  const { onOpen } = disclosure;
  const [isCreateEstimatePopoverVisible, setIsCreateEstimatePopoverVisible] = useState(false);
  const [isDMSImportActive, setIsDMSImportActive] = useState(false);

  useFetchAvailableDMSServices();

  const handleConfirm = () => {
    trackAppEvent(TRACKING_EVENT_CREATE_NEW_ESTIMATE_CONFIRM);
    dispatch(createNewEstimate({ vehicle }));
    history.push(ROUTER_ESTIMATE);
  };

  function createEstimate() {
    trackAppEvent(TRACKING_EVENT_CREATE_NEW_ESTIMATE);
    onOpen();
  }

  function importDMSOrder(orderNumber: string) {
    dispatch(createEstimateFromDMSRequest({ orderNumber }));
  }

  return (
    <Container data-cy={'estimate-history-page'}>
      <CenterFlex>
        <Dialog
          disclosure={disclosure}
          title={t('common.dialog.actionRequired', 'Action required')}
          description={t(
            'estimate.create_new.description',
            "You're about to create a new estimate, your current one will be saved in your estimate  history.",
          )}
          icon={SaveIcon}
          status={'info'}
          handleConfirm={handleConfirm}
        />
      </CenterFlex>
      <Flex direction={'row'} align={'center'} justify={'space-between'}>
        <Text type={'h1'} disableGutter>
          {t('estimate.history', 'History')}
        </Text>
        <Box data-cy={'create-new-estimate-button'}>
          <WhiteButton
            size={'middle'}
            onClick={() => {
              if (!isDMSAvailable) {
                createEstimate();
                return;
              }
              setIsCreateEstimatePopoverVisible(true);
            }}
          >
            {t('estimate.create_new', 'Create new estimate')}
          </WhiteButton>
          <SPopover
            id={'create-new-estimate-popover'}
            getPopupContainer={(trigger) => trigger.parentElement ?? trigger}
            content={
              <CreateEstimateButton
                createEstimate={createEstimate}
                isDMSAvailable={isDMSAvailable}
                importDMSOrder={importDMSOrder}
                setIsCreateEstimatePopoverVisible={setIsCreateEstimatePopoverVisible}
                isDMSImportActive={isDMSImportActive}
                setIsDMSImportActive={setIsDMSImportActive}
              />
            }
            trigger={'click'}
            placement={'bottomRight'}
            visible={isCreateEstimatePopoverVisible}
            onVisibleChange={(visible) => {
              setIsCreateEstimatePopoverVisible(visible);
              if (visible === false) {
                setIsDMSImportActive(false);
              }
            }}
          />
        </Box>
      </Flex>
      <Box height={20} />
      <HistoryTab />
    </Container>
  );
};
